import { Button, ButtonSize, ScrollLock } from '@farmasiet/frontendcore-reactcomponents';
import classNames from 'classnames';
import * as React from 'react';
import { useEffect } from 'react';

import { ContentReference } from '../../../ContentReference.csharp';
import { getTranslation, keys } from '../../../Resources/Translations';
import Accordion, { ItemStatus } from '../../ReactComponents/Accordion/Accordion';
import AccordionItem from '../../ReactComponents/Accordion/AccordionItem';
import { PriceRange, PriceRangeValues } from '../PriceRange/PriceRange';
import { LoadedTag } from '../TagList/TagList';
import { CategoriesTree } from './CategoriesTree/CategoriesTree';
import { FilterOptionViewModel } from './FilterOptionViewModel.csharp';
import { FilterViewModel } from './FilterViewModel.csharp';
import { SelectedOption } from './SelectedOption';

interface ProductFiltersProps {
    links: FilterViewModel;
    filters: FilterViewModel[];
    searchQuery: string;
    selectedOptions: SelectedOption[];
    productAmount: number;
    priceRange: PriceRangeValues;
    currencyCode: string;
    onChangeOption: (
        filterKey: string,
        subfilterKey: string | null,
        optionKey: string,
        isSelected: boolean,
        isSubfilter?: boolean,
        callback?: () => void
    ) => void;
    onChangePriceRange: (priceValues: number[]) => void;
    onChangePriceInputMin: (value: string) => void;
    onChangePriceInputMax: (value: string) => void;
    onAfterChangePrice: () => void;
    onReset: () => void;
    link: ContentReference;
    leadInText?: string;
    requestId?: string;
    searchTagsUrl?: string;
    onAddOption?: (filterKey: string, subFilterKey: string | null, optionKey: string, isSelected: boolean) => void;
    onClick?: (searchId: string, tagName: string, removeSearchQuery: boolean, filters: { key: string; value: string }[], callback?: () => void) => void;
    limit?: number;
    isProductsLoading?: boolean;
    loadedTags: LoadedTag[];
}

export const ProductFilters = (props: ProductFiltersProps) => {
    const [isVisible, setIsVisible] = React.useState(false);
    const [expandedFilters, setExpandedFilters] = React.useState<string[]>([]);
    const filtersRef = React.useRef<HTMLDivElement>(null);
    const filtersContainerRef = React.useRef<HTMLDivElement>(null);
    const filtersContentRef = React.useRef<HTMLDivElement>(null);
    const [categoryFilters, setCategoryFilters] = React.useState<FilterViewModel>();

    useEffect(() => {
        filtersRef.current && filtersRef.current.addEventListener('click', (event) => handleClickOutside(event));
    });

    useEffect(() => {
        const categoryFiltersIndex = props.filters.findIndex((x) => x.key === 'category');

        if (categoryFiltersIndex >= 0) {
            setCategoryFilters(props.filters[categoryFiltersIndex]);
        }
    }, [props]);

    const renderFilter = (filterKey: string, options: FilterOptionViewModel[]) => {
        const items = options.map((option, index) => {
            const optionId = filterKey + option.key;
            return (
                <li key={index}>
                    <div className="CustomCheckbox">
                        <input
                            type="checkbox"
                            value={option.key}
                            checked={option.isSelected}
                            onChange={() => props.onChangeOption(filterKey, null, option.key, !option.isSelected)}
                            id={optionId}
                        />
                        <label htmlFor={optionId}>
                            <span>{option.value}</span> <span>({option.count})</span>
                        </label>
                    </div>
                </li>
            );
        });

        return <>{items.length > 0 && <ul className="Form__FieldList">{items}</ul>}</>;
    };

    const renderSubcategories = () => {
        const linkQuery = getLink();

        return props.links && props.links.options?.length > 0 ? (
            <div className="ProductFilters__Subcategories">
                {props.links.url && (
                    <a
                        href={`${props.links.url}${linkQuery}`}
                        className={classNames(
                            'ProductFilters__ItemHeader ProductFilters__ItemHeaderParent ProductFilters__SelectedSubcategory',
                            props.link === props.links.link && 'ProductFilters__SelectedSubcategory'
                        )}
                    >
                        {props.links.label}
                    </a>
                )}
                {!props.links.url && <div className={'ProductFilters__ItemHeader'}>{props.links.label}</div>}
                <ul className="Form__FieldList">
                    {props.links.options.map((option) => (
                        <li key={option.key}>
                            <div>
                                <a href={`${option.url}${linkQuery}`} className={props.link === option.link ? 'ProductFilters__SelectedSubcategory' : ''}>
                                    {option.value} ({option.count})
                                </a>
                            </div>
                        </li>
                    ))}
                </ul>
            </div>
        ) : null;
    };

    const getLink = () => {
        const index = props.searchQuery.indexOf('page=');
        return index < 0 ? props.searchQuery : props.searchQuery.substr(0, index) + 'page=1' + props.searchQuery.substr(index + 6);
    };

    const handleClickOutside = (event: MouseEvent) => {
        const filtersContainer = filtersContainerRef.current;

        if (filtersContainer && !filtersContainer.contains(event.target as Node)) {
            setIsVisible(false);
        }
    };

    const getFilterKeys = (): string[] => {
        const keys = props.filters.map((x) => x.key);
        keys.push('price');
        const categoryIndex = keys.indexOf('category');
        categoryIndex >= 0 && keys.splice(categoryIndex, 1);
        return keys;
    };

    const getFiltersWithoutCategory = (): FilterViewModel[] => {
        const categoryFilterIndex = props.filters.findIndex((x) => x.key === 'category');
        return categoryFilterIndex > -1 ? props.filters.slice(0, categoryFilterIndex).concat(props.filters.slice(categoryFilterIndex + 1)) : props.filters;
    };

    const buildItemStatus = (): ItemStatus[] => {
        const filtersWithoutCategory = getFiltersWithoutCategory();
        const newStatus = filtersWithoutCategory.map((x) => {
            const isExpanded = expandedFilters.indexOf(x.key) > -1;
            return {
                isExpanded: isExpanded,
                isActive: isExpanded
            };
        });

        const isPriceExpanded = expandedFilters.indexOf('price') > -1;
        newStatus.push({ isExpanded: isPriceExpanded, isActive: isPriceExpanded });
        return newStatus;
    };
    return (
        <aside className="ProductFilters">
            <ScrollLock isLocked={isVisible} bodyClassName="ProductFiltersVisible" />
            <h2 className={classNames('ProductFilters__Heading')}>
                {getTranslation(keys['productFilters.filters'])}
                <button className={classNames('ProductFilters__ToggleButton')} onClick={() => setIsVisible(true)}>
                    <span className="visuallyHidden">{getTranslation(keys['productFilters.open'])}</span>
                </button>
            </h2>
            <div ref={filtersRef} className={classNames('ProductFilters__Overlay', isVisible && 'ProductFilters__Overlay--Visible')}>
                <div ref={filtersContainerRef} className={classNames('ProductFilters__OverlayInner')}>
                    <div className={classNames('ProductFilters__Header')}>
                        <h2>
                            {getTranslation(keys['productFilters.filters'])}
                            <span>
                                {props.productAmount} {getTranslation(keys['productFilters.products'])}
                            </span>
                        </h2>
                    </div>
                    <div className="ProductFilters__Content" ref={filtersContentRef}>
                        {categoryFilters && categoryFilters.options.length > 0 && (
                            <div className="ProductFilters__Subcategories">
                                <span className={classNames('ProductFilters__ItemHeader ProductFilters__ItemHeaderParent ProductFilters__SelectedSubcategory')}>
                                    {getTranslation(keys['filters.category'])}
                                </span>
                                <CategoriesTree
                                    categoryFilters={categoryFilters}
                                    onChangeOption={props.onChangeOption}
                                    container={filtersContentRef.current}
                                    isProductsLoading={props.isProductsLoading}
                                />
                            </div>
                        )}
                        {renderSubcategories()}

                        <Accordion
                            className="Form"
                            allowMultipleItems={true}
                            itemsStatus={buildItemStatus()}
                            labelClassName="ProductFilters__ItemHeader"
                            enableAutoScrolling
                            scrollOnlyOnMobile
                            onActiveItemChanged={(changedItemsStatus) => {
                                const expanded: string[] = [];
                                const keys = getFilterKeys();
                                changedItemsStatus.forEach((status, index) => {
                                    if (status.isActive && keys[index]) {
                                        expanded.push(keys[index]);
                                    }
                                });

                                setExpandedFilters(expanded);
                            }}
                        >
                            {getFiltersWithoutCategory().map((filter) => {
                                return (
                                    <AccordionItem key={filter.key} label={filter.label}>
                                        {renderFilter(filter.key, filter.options)}
                                    </AccordionItem>
                                );
                            })}

                            <AccordionItem label={getTranslation(keys['filters.price'])}>
                                <PriceRange
                                    values={props.priceRange}
                                    currencyCode={props.currencyCode}
                                    onMinChange={props.onChangePriceInputMin}
                                    onMaxChange={props.onChangePriceInputMax}
                                    onAfterChange={props.onAfterChangePrice}
                                    onChange={(updatedValues) => props.onChangePriceRange(updatedValues)}
                                />
                            </AccordionItem>
                        </Accordion>
                    </div>
                    <div className="ProductFilters__Footer">
                        {props.selectedOptions.length > 0 && (
                            <button className={classNames('ProductFilters__Reset')} onClick={() => props.onReset()}>
                                <span>{getTranslation(keys['productFilters.clear'])}</span>
                            </button>
                        )}
                        <Button size={ButtonSize.Small} onClick={() => setIsVisible(false)} className={classNames('ProductFilters__Apply')}>
                            {getTranslation(keys['productFilters.seeResults'])}
                        </Button>
                    </div>
                </div>
            </div>
        </aside>
    );
};
